import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";

/**
 * Barras horizontales de los estados finales por símbolo del glosario 2 (IES).
 *
 * Réplica de graficar_estados_similitud(final_states, similarity_matrix) del
 * notebook del autor: una barra por símbolo IES cuya longitud es su similitud
 * máxima contra el Master, coloreada según el estado final del símbolo.
 *
 * Se dibuja con CSS en vez de @mui/x-charts porque el número de símbolos es
 * variable y aquí cada barra necesita su propio color por estado (no por serie),
 * que es justamente lo que el BarChart de MUI no hace de forma directa.
 */

const LEL_COLORS = {
  Similitud: "#00008B",
  Diferencia: "#1E90FF",
  Omisión: "#87CEFA",
  "No similitud": "#87CEFA",
};

// Orden de apilado/leyenda igual al del notebook.
const ORDEN_LEYENDA = ["Similitud", "Diferencia", "Omisión"];

const MaterialEstadosSimilitud = ({ barData }) => {
  // barData = visualization_data.bar_data => { estado: [{name, value}, ...] }
  const filas = React.useMemo(() => {
    if (!barData) return [];
    return Object.entries(barData).flatMap(([estado, items]) =>
      (items || []).map((item) => ({
        name: item.name,
        value: typeof item.value === "number" ? item.value : 0,
        estado,
      }))
    );
  }, [barData]);

  if (!filas.length) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Sin datos de estados
        </Typography>
      </Box>
    );
  }

  // Estados realmente presentes, para no mostrar leyenda de categorías vacías.
  const estadosPresentes = ORDEN_LEYENDA.filter((e) =>
    filas.some((f) => f.estado === e)
  );

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Leyenda */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          justifyContent: "flex-end",
          flexWrap: "wrap",
          mb: 0.5,
          flexShrink: 0,
        }}
      >
        {estadosPresentes.map((estado) => (
          <Box
            key={estado}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                backgroundColor: LEL_COLORS[estado],
                border: "1px solid rgba(0,0,0,0.15)",
              }}
            />
            <Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
              {estado}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Barras (scroll propio si hay muchos símbolos) */}
      {/* Las filas se reparten el alto disponible: con pocos símbolos se
          estiran para llenar la tarjeta (sin hueco al final) y con muchos
          caen al alto mínimo y aparece el scroll. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pr: 0.5,
          display: "flex",
          flexDirection: "column",
          justifyContent: filas.length > 18 ? "flex-start" : "space-evenly",
        }}
      >
        {filas.map((fila, idx) => (
          <Box
            key={`${fila.name}-${idx}`}
            sx={{
              display: "flex",
              alignItems: "center",
              flex: filas.length > 18 ? "0 0 auto" : "1 1 0",
              minHeight: 18,
              maxHeight: 44,
              mb: filas.length > 18 ? 0.5 : 0,
            }}
          >
            <Tooltip title={fila.name} placement="left">
              <Typography
                sx={{
                  width: "130px",
                  minWidth: "130px",
                  fontSize: "9px",
                  textAlign: "right",
                  pr: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "text.secondary",
                }}
              >
                {fila.name}
              </Typography>
            </Tooltip>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                backgroundColor: "#f5f5f5",
                borderRadius: "2px",
                height: "100%",
                maxHeight: 26,
                minHeight: 14,
                position: "relative",
              }}
            >
              <Tooltip
                title={`${fila.name} — ${fila.estado}: ${fila.value.toFixed(3)}`}
                arrow
              >
                <Box
                  sx={{
                    // El eje va de 0 a 1 (similitud), igual que en el notebook.
                    width: `${Math.max(0, Math.min(1, fila.value)) * 100}%`,
                    height: "100%",
                    backgroundColor:
                      LEL_COLORS[fila.estado] || LEL_COLORS["No similitud"],
                    borderRadius: "2px",
                    transition: "width 0.3s ease",
                  }}
                />
              </Tooltip>
            </Box>

            <Typography
              sx={{
                width: "34px",
                fontSize: "9px",
                pl: 0.5,
                color: "text.secondary",
              }}
            >
              {fila.value.toFixed(2)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        sx={{
          fontSize: "9px",
          color: "text.secondary",
          textAlign: "center",
          flexShrink: 0,
          pt: 0.5,
        }}
      >
        Similitud máxima por símbolo (0 – 1)
      </Typography>
    </Box>
  );
};

export default MaterialEstadosSimilitud;
